/**
 * Line view model — pure derivation.
 *
 * How the day's assignments lay out by LINE: work center → step → phase →
 * card, with the empty crew slots a step still has. Nothing here touches
 * React or the server.
 */
import { parseISO } from "date-fns";

import { LaborStandard } from "../../../../../services/labor-standards-api";
import { AvailabilityRow, RosterAssignment } from "../../../../../services/roster-assignments-api";
import { ShiftInstance } from "../../../../../services/shifts-api";
import { ManufacturingOrder } from "../../../production-plan/utils/calculations";
import { batchOf, orderIdOf } from "../../../utils/orders";
import { findStandardForOrder, isMachinePhase, placeOrderPhases } from "../../../utils/standard-matching";
import { dayStartMs, hoursInto, hoursIntoDate, localDay, toLocalIso } from "../../../utils/time";
import { AvailabilityIndex, isConfirmedRow } from "./availability";

export interface LaneCard {
	id: number;
	workerId: string;
	workerName: string;
	jobRole: string;
	segmentSeq: number | null;
	/** Decimal hours into the day, clipped to the day. */
	start: number;
	end: number;
	isManuallyAssigned: boolean;
	/** Null when the row names no order at all. */
	workOrderId: number | null;
	phaseId: number | null;
	/** The shift the assignment sits on, when the server sent it. */
	shiftId: string | null;
	/** yyyy-MM-dd of the day this card was built for (a midnight-spanning block is a card on each day). */
	dayIso: string;
	/**
	 * Why the person is not expected on this shift (or asking not to be), when
	 * the availability rows say so; null when they are simply expected.
	 */
	unavailable: AvailabilityRow | null;
	/** Confirmed out (`unavailable` is not a mere pending request): the card does not count as crew. */
	isOut: boolean;
}

/**
 * One open crew line on a phase, one shift segment long. A step that crosses
 * a shift handover is cut at the plant's shift boundaries so the fill picker
 * asks for one shift, not the whole step.
 */
export interface LaneSlot {
	/** Decimal hours into the day, clipped to the day. */
	start: number;
	end: number;
	/** Absolute window the assignment would cover. */
	startIso: string;
	endIso: string;
	/** The phase this crew line is on (null only for the orphan bucket, which has no slots). */
	phaseId: number | null;
}

export interface LanePhase {
	/** Null for the catch-all bucket of cards whose phase is not on the standard. */
	phaseId: number | null;
	label: string;
	jobRole: string;
	jobRoleId: number | null;
	start: number;
	end: number;
	/** Absolute window, for the fill picker / manual assign. */
	startIso: string;
	endIso: string;
	needed: number;
	cards: LaneCard[];
	/** Workers on the phase who are confirmed out — listed, not counted. */
	outCount: number;
	/** Crew lines still open on this phase that touch the day (= `slots.length`). */
	emptySlots: number;
	/** The open crew lines, one per shift segment still short of crew. */
	slots: LaneSlot[];
}

/**
 * The crew lines of a step that share an operation and a job role, read as
 * ONE group: a standard with two one-person coating phases is "Coating 2/2",
 * not two "Coating 1/1" lines. Pure presentation layer over `LanePhase`; the
 * phases themselves are untouched and a drop still lands on one phase.
 */
export interface LaneCrewGroup {
	/** `${operationName}::${jobRole}`, unique within a step. */
	key: string;
	operationName: string;
	jobRole: string;
	jobRoleId: number | null;
	/** Distinct workers across the group's phases who are actually expected (the out ones are not counted). */
	have: number;
	/** Distinct workers across the group's phases who are confirmed out. */
	outCount: number;
	/** Heads needed across the group's phases. */
	needed: number;
	start: number;
	end: number;
	cards: LaneCard[];
	/** The open crew lines of every phase in the group, each one shift segment long, in time order. */
	slots: LaneSlot[];
	phaseIds: number[];
	phases: LanePhase[];
	/** The phase a drop on the group lands on: the first still short of crew, else the first. Null for the orphan bucket. */
	dropPhaseId: number | null;
}

export interface LaneStep {
	workOrderId: number;
	/** The product: the only text on the step's header. */
	title: string;
	/** Batch / SKU label ("Meto 12"), shown on hover and click. */
	subtitle: string;
	/** The batch number on its own ("11", "B-204"), when the order has one. */
	batch?: string | null;
	/** Everything else about the order, label → value, for the hover and click details. */
	details: StepDetail[];
	start: number;
	end: number;
	crewHave: number;
	crewNeeded: number;
	/** Crew on the step who are confirmed out — short in the coverage, still on the card. */
	crewOut: number;
	phases: LanePhase[];
}

export interface StepDetail {
	label: string;
	value: string;
}

export interface LaneRow {
	workcenter: string;
	steps: LaneStep[];
	/** Cards whose order is not one of the day's steps (a step from another week). */
	loose: LaneCard[];
}

const clip = (value: number): number => Math.min(24, Math.max(0, value));

export const stepTitle = (order: ManufacturingOrder): string =>
	order.productName || (order.sku ?? "").split("/")[0].trim() || `WO #${order.id}`;

/** What a job card calls the order: the full product name and its batch ("Mixed Salts Drug Seal Coating #1"). */
export const jobLabel = (step: Pick<LaneStep, "title" | "batch">): string => (step.batch ? `${step.title} #${step.batch}` : step.title);

const stepSubtitle = (order: ManufacturingOrder): string =>
	[order.batchNumber, order.sku && order.sku !== order.productName ? order.sku : null]
		.filter(Boolean)
		.join(" · ");

/** The order's facts for the step details (empty values left out). */
export const stepDetails = (order: ManufacturingOrder): StepDetail[] =>
	[
		{ label: "Batch", value: stepSubtitle(order) },
		{ label: "Item #", value: order.productItemNumber ?? "" },
		{ label: "Type", value: order.orderType && order.orderType !== "production" ? order.orderType : "" },
		{ label: "Quantity", value: order.quantity ? order.quantity.toLocaleString() : "" },
		{ label: "Labor standard", value: order.laborStandardName ?? "" },
		{ label: "Job order", value: `#${order.id}` },
	].filter((detail) => detail.value);

const toCard = (a: RosterAssignment, dayStart: number, dayIso: string, availability?: AvailabilityIndex): LaneCard => {
	const unavailable = availability?.lookup(String(a.workerId), a.shiftId ?? null, dayIso) ?? null;
	return {
		id: a.id,
		workerId: String(a.workerId),
		workerName: a.workerName,
		jobRole: a.jobRoleName,
		segmentSeq: a.segmentSeq ?? null,
		start: clip(hoursInto(a.startTime, dayStart)),
		end: clip(hoursInto(a.endTime, dayStart)),
		isManuallyAssigned: Boolean(a.isManuallyAssigned),
		workOrderId: orderIdOf(a) ?? null,
		phaseId: a.laborStandardPhaseId ?? null,
		shiftId: a.shiftId ?? null,
		dayIso,
		unavailable,
		isOut: unavailable !== null && isConfirmedRow(unavailable),
	};
};

const distinctWorkers = (cards: LaneCard[]): number => new Set(cards.map((card) => card.workerId)).size;
/** Distinct workers who are actually expected — the ones that count as crew. */
const distinctPresent = (cards: LaneCard[]): number => distinctWorkers(cards.filter((card) => !card.isOut));
/** Distinct workers confirmed out. */
const distinctOut = (cards: LaneCard[]): number => distinctWorkers(cards.filter((card) => card.isOut));

export interface BuildLaneRowsArgs {
	day: Date;
	/** The plant's work centers, in display order. Centers found only in the data are appended. */
	workcenters: string[];
	orders: ManufacturingOrder[];
	standards: LaborStandard[];
	assignments: RosterAssignment[];
	/** Phase id -> operation name, so a phase reads "Granulation" not "Operator". */
	operationNames?: Map<number, string>;
	/**
	 * The plant's shift instances. When given, an open crew line on a phase that
	 * crosses a shift handover is offered per shift segment; without them the
	 * phase's whole window is one slot.
	 */
	shifts?: ShiftInstance[];
	/**
	 * Who is out (or asking to be). A card whose worker is confirmed out stays
	 * on its line but does not count as crew; no "+ Assign" slot is opened for
	 * it — the supervisor replaces the person from the card's menu.
	 */
	availability?: AvailabilityIndex;
}

interface Segment {
	start: number;
	end: number;
}

/**
 * Cut a phase window at the plant's shift boundaries — the same cut points the
 * engine uses: segment = [max(start, shiftStart), min(end, shiftEnd)]. Time
 * between shifts (a gap with no shift) is not a segment. With no shift
 * overlapping the window at all, the whole window is one segment, so a step
 * on a day without shifts loaded stays fillable.
 */
export function shiftSegments(start: number, end: number, shifts: ShiftInstance[] | undefined): Segment[] {
	if (end <= start) {
		return [];
	}
	const seen = new Set<string>();
	const segments: Segment[] = [];
	(shifts ?? []).forEach((shift) => {
		const shiftStart = parseISO(shift.startDateTime).getTime();
		const shiftEnd = parseISO(shift.endDateTime).getTime();
		const segStart = Math.max(start, shiftStart);
		const segEnd = Math.min(end, shiftEnd);
		if (segEnd <= segStart) {
			return;
		}
		const key = `${segStart}:${segEnd}`;
		if (!seen.has(key)) {
			seen.add(key);
			segments.push({ start: segStart, end: segEnd });
		}
	});
	if (!segments.length) {
		return [{ start, end }];
	}
	// Several shift instances can cover the same clock on different work
	// centers; a segment inside another is the same crew line twice.
	const sorted = segments.sort((a, b) => a.start - b.start || b.end - a.end);
	const inside = (seg: Segment, other: Segment): boolean => other.start <= seg.start && other.end >= seg.end;
	return sorted.filter((seg, i) => !sorted.some((other, j) => j !== i && inside(seg, other)));
}

/**
 * The open crew lines on a phase that touch the day: for each shift segment
 * of the phase window, `needed` minus the distinct workers whose assignment on
 * the phase overlaps that segment.
 */
function openSlots(
	phaseId: number,
	phaseStart: Date,
	phaseEnd: Date,
	needed: number,
	onPhase: RosterAssignment[],
	shifts: ShiftInstance[] | undefined,
	dayStart: number,
	dayEnd: number
): LaneSlot[] {
	const heads = Math.ceil(needed);
	if (heads <= 0) {
		return [];
	}
	const slots: LaneSlot[] = [];
	shiftSegments(phaseStart.getTime(), phaseEnd.getTime(), shifts)
		.filter((seg) => seg.start < dayEnd && seg.end > dayStart)
		.forEach((seg) => {
			const covering = new Set(
				onPhase
					.filter((a) => parseISO(a.startTime).getTime() < seg.end && parseISO(a.endTime).getTime() > seg.start)
					.map((a) => String(a.workerId))
			);
			for (let i = covering.size; i < heads; i += 1) {
				slots.push({
					start: clip((seg.start - dayStart) / 3600000),
					end: clip((seg.end - dayStart) / 3600000),
					startIso: toLocalIso(new Date(seg.start)),
					endIso: toLocalIso(new Date(seg.end)),
					phaseId,
				});
			}
		});
	return slots;
}

/**
 * Lanes = work centers; under each, the steps running that day with their
 * labor phases; under each phase, one card per assignment and one empty
 * slot per crew line nobody is on. A machine phase needs nobody and is
 * left out — only the staffing picture is drawn here.
 */
export function buildLaneRows({
	day,
	workcenters,
	orders,
	standards,
	assignments,
	operationNames,
	shifts,
	availability,
}: BuildLaneRowsArgs): LaneRow[] {
	const dayStart = dayStartMs(day);
	const dayEnd = dayStart + 86400000;
	const dayIso = localDay(day);

	const dayOrders = orders.filter((order) => {
		if (!order.workstation) {
			return false;
		}
		const start = parseISO(order.startTime).getTime();
		const end = order.endTime ? parseISO(order.endTime).getTime() : start;
		return start < dayEnd && (end > dayStart || start >= dayStart);
	});
	const orderById = new Map(dayOrders.map((order) => [order.id, order]));

	const dayAssignments = assignments.filter(
		(a) => parseISO(a.startTime).getTime() < dayEnd && parseISO(a.endTime).getTime() > dayStart
	);
	const byOrder = new Map<number, RosterAssignment[]>();
	const loose = new Map<string, LaneCard[]>();
	dayAssignments.forEach((a) => {
		const orderId = orderIdOf(a);
		if (orderId !== undefined && orderById.has(orderId)) {
			byOrder.set(orderId, [...(byOrder.get(orderId) || []), a]);
			return;
		}
		const center = a.workstation || "Unassigned";
		loose.set(center, [...(loose.get(center) || []), toCard(a, dayStart, dayIso, availability)]);
	});

	const rows = new Map<string, LaneRow>();
	const rowFor = (workcenter: string): LaneRow => {
		let row = rows.get(workcenter);
		if (!row) {
			row = { workcenter, steps: [], loose: [] };
			rows.set(workcenter, row);
		}
		return row;
	};
	workcenters.forEach(rowFor);

	dayOrders
		.sort((a, b) => parseISO(a.startTime).getTime() - parseISO(b.startTime).getTime())
		.forEach((order) => {
			const onOrder = byOrder.get(order.id) || [];
			const cards = onOrder.map((a) => toCard(a, dayStart, dayIso, availability));
			const standard = findStandardForOrder(order, standards);
			const placed = standard ? placeOrderPhases(order, standard).filter((p) => !isMachinePhase(p.phase)) : [];
			const phaseIds = new Set(placed.map((p) => p.phase.id));

			const phases: LanePhase[] = placed.map((p) => {
				const mine = cards.filter((card) => card.phaseId === p.phase.id);
				const needed = Number(p.phase.workersRequired) || 0;
				const slots = openSlots(
					p.phase.id,
					p.start,
					p.end,
					needed,
					onOrder.filter((a) => a.laborStandardPhaseId === p.phase.id),
					shifts,
					dayStart,
					dayEnd
				);
				return {
					phaseId: p.phase.id,
					label: operationNames?.get(p.phase.id) || p.operation.name || p.phase.jobRole,
					jobRole: p.phase.jobRole,
					jobRoleId: p.phase.jobRoleId ?? null,
					start: clip(hoursIntoDate(p.start, dayStart)),
					end: clip(hoursIntoDate(p.end, dayStart)),
					startIso: toLocalIso(p.start),
					endIso: toLocalIso(p.end),
					needed,
					cards: mine.sort((x, y) => x.start - y.start || (x.segmentSeq ?? 0) - (y.segmentSeq ?? 0)),
					outCount: distinctOut(mine),
					emptySlots: slots.length,
					slots,
				};
			});

			// Cards on a phase the standard no longer has (or no phase at all) still
			// belong to the step — they go in one unlabelled bucket, never dropped.
			const orphans = cards.filter((card) => card.phaseId === null || !phaseIds.has(card.phaseId));
			if (orphans.length) {
				const start = Math.min(...orphans.map((c) => c.start));
				const end = Math.max(...orphans.map((c) => c.end));
				phases.push({
					phaseId: null,
					label: "Other",
					jobRole: "",
					jobRoleId: null,
					start,
					end,
					startIso: toLocalIso(new Date(dayStart + start * 3600000)),
					endIso: toLocalIso(new Date(dayStart + end * 3600000)),
					needed: 0,
					cards: orphans.sort((x, y) => x.start - y.start),
					outCount: distinctOut(orphans),
					emptySlots: 0,
					slots: [],
				});
			}

			const orderStart = parseISO(order.startTime).getTime();
			const orderEnd = order.endTime ? parseISO(order.endTime).getTime() : orderStart;
			const crewNeeded = phases.reduce((total, phase) => total + Math.ceil(phase.needed), 0);
			// Someone confirmed out is on the card but not on the line: they are
			// short in the count, and the headline coverage follows.
			const crewHave = phases.reduce(
				(total, phase) =>
					total + (phase.needed > 0 ? Math.min(Math.ceil(phase.needed), distinctPresent(phase.cards)) : 0),
				0
			);
			const crewOut = phases.reduce((total, phase) => total + phase.outCount, 0);

			rowFor(order.workstation).steps.push({
				workOrderId: order.id,
				title: stepTitle(order),
				subtitle: stepSubtitle(order),
				batch: batchOf(order),
				details: stepDetails(order),
				start: clip(hoursInto(order.startTime, dayStart)),
				end: clip(hoursIntoDate(new Date(Math.max(orderEnd, orderStart)), dayStart)),
				crewHave,
				crewNeeded,
				crewOut,
				phases,
			});
		});

	loose.forEach((cards, workcenter) => {
		rowFor(workcenter).loose = cards.sort((x, y) => x.start - y.start);
	});

	return Array.from(rows.values());
}

/**
 * Merge a step's phases into crew groups by (operation name, job role), in
 * order of first appearance. A machine phase needs nobody and is never a
 * phase here, so never a group either. The orphan bucket (cards on no known
 * phase) stays its own group with no drop target. Cards already know whether
 * their worker is out (see `buildLaneRows`' `availability`), so `have`
 * counts only the people actually expected and `outCount` the rest.
 */
export function groupCrewLines(phases: LanePhase[]): LaneCrewGroup[] {
	const groups = new Map<string, LaneCrewGroup>();
	phases.forEach((phase) => {
		const key = phase.phaseId === null ? "other" : `${phase.label}::${phase.jobRole}`;
		let group = groups.get(key);
		if (!group) {
			group = {
				key,
				operationName: phase.label,
				jobRole: phase.jobRole,
				jobRoleId: phase.jobRoleId,
				have: 0,
				outCount: 0,
				needed: 0,
				start: phase.start,
				end: phase.end,
				cards: [],
				slots: [],
				phaseIds: [],
				phases: [],
				dropPhaseId: null,
			};
			groups.set(key, group);
		}
		group.jobRoleId = group.jobRoleId ?? phase.jobRoleId;
		group.start = Math.min(group.start, phase.start);
		group.end = Math.max(group.end, phase.end);
		group.cards.push(...phase.cards);
		group.slots.push(...phase.slots);
		group.needed += Math.ceil(phase.needed);
		group.phases.push(phase);
		if (phase.phaseId !== null) {
			group.phaseIds.push(phase.phaseId);
		}
	});
	return Array.from(groups.values()).map((group) => {
		const short = group.phases.find(
			(phase) => phase.phaseId !== null && (phase.slots.length > 0 || distinctPresent(phase.cards) < Math.ceil(phase.needed))
		);
		return {
			...group,
			have: distinctPresent(group.cards),
			outCount: distinctOut(group.cards),
			cards: [...group.cards].sort((x, y) => x.start - y.start || (x.segmentSeq ?? 0) - (y.segmentSeq ?? 0)),
			slots: [...group.slots].sort((x, y) => x.start - y.start),
			dropPhaseId: short?.phaseId ?? group.phaseIds[0] ?? null,
		};
	});
}

/** The visible hour window of the lanes, same rules as the worker view. */
export function laneHourWindow(rows: LaneRow[]): { start: number; end: number } {
	const spans = rows.flatMap((row) => [
		...row.steps.map((step) => ({ start: step.start, end: step.end })),
		...row.loose.map((card) => ({ start: card.start, end: card.end })),
	]);
	if (!spans.length) {
		return { start: 5, end: 14 };
	}
	const start = Math.floor(Math.min(...spans.map((s) => s.start)));
	const end = Math.ceil(Math.max(...spans.map((s) => s.end)));
	return { start, end: Math.max(end, start + 4) };
}
